import React, { useState, useEffect, useCallback } from 'react';
import coachNotesService from '../../services/endpoints/coachNotesService';
import { CoachAthleteNote, CoachAthleteNoteTag, CreateCoachNoteRequest } from '../../types';

// ── Tag config ────────────────────────────────────────────────────────────────

const TAG_LABELS: Record<CoachAthleteNoteTag, string> = {
  HighAnxiety:          'High Anxiety',
  RoboticallyCompliant: 'Robotically Compliant',
  DrugSensitivity:      'Drug Sensitivity',
  HighCompliance:       'High Compliance',
  Inconsistent:         'Inconsistent',
  CheckInRisk:          'Check-In Risk',
  Cautious:             'Cautious',
};

const ALL_TAGS = Object.keys(TAG_LABELS) as CoachAthleteNoteTag[];

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  athleteUserId: string;
  athleteName: string;
  relationshipId: string;
  onClose: () => void;
  onNotesChanged: (hasNotes: boolean) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const CoachAthleteNotesDrawer: React.FC<Props> = ({
  athleteUserId,
  athleteName,
  relationshipId,
  onClose,
  onNotesChanged,
}) => {
  const [notes, setNotes]         = useState<CoachAthleteNote[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // New-note form
  const [newContent, setNewContent] = useState('');
  const [newTags, setNewTags]       = useState<CoachAthleteNoteTag[]>([]);
  const [saving, setSaving]         = useState(false);

  // Inline edit state
  const [editingId, setEditingId]       = useState<string | null>(null);
  const [editContent, setEditContent]   = useState('');
  const [editTags, setEditTags]         = useState<CoachAthleteNoteTag[]>([]);
  const [editSaving, setEditSaving]     = useState(false);

  // Delete confirm
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await coachNotesService.getAthleteNotes(athleteUserId);
      setNotes(data);
    } catch {
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
    }
  }, [athleteUserId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const toggleTag = (
    tag: CoachAthleteNoteTag,
    current: CoachAthleteNoteTag[],
    set: (v: CoachAthleteNoteTag[]) => void
  ) => {
    set(current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag]);
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!newContent.trim()) return;
    setSaving(true);
    try {
      const payload: CreateCoachNoteRequest = {
        athleteUserId,
        relationshipId,
        content: newContent.trim(),
        tags: newTags,
      };
      const created = await coachNotesService.createNote(payload);
      const updated = [created, ...notes];
      setNotes(updated);
      setNewContent('');
      setNewTags([]);
      onNotesChanged(updated.length > 0);
    } catch {
      setError('Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note: CoachAthleteNote) => {
    setEditingId(note.noteId);
    setEditContent(note.content);
    setEditTags(note.tags as CoachAthleteNoteTag[]);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
    setEditTags([]);
  };

  const handleUpdate = async (noteId: string) => {
    if (!editContent.trim()) return;
    setEditSaving(true);
    try {
      const updated = await coachNotesService.updateNote(noteId, {
        content: editContent.trim(),
        tags: editTags,
      });
      setNotes(notes.map(n => n.noteId === noteId ? updated : n));
      cancelEdit();
    } catch {
      setError('Failed to update note.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (noteId: string) => {
    try {
      await coachNotesService.deleteNote(noteId);
      const updated = notes.filter(n => n.noteId !== noteId);
      setNotes(updated);
      setDeleteTargetId(null);
      onNotesChanged(updated.length > 0);
    } catch {
      setError('Failed to delete note.');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-800 border-l border-dark-700 z-50 flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-dark-700 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">Private Notes</h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-dark-900 text-gray-400 border border-dark-700">
                🔒 Coach only
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-0.5">{athleteName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors text-xl leading-none mt-0.5"
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* ── Add Note Form ── */}
          <div className="bg-dark-700 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-300">Add Note</h3>

            {/* Tag chips */}
            <div className="flex flex-wrap gap-2">
              {ALL_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag, newTags, setNewTags)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    newTags.includes(tag)
                      ? 'bg-gold-500/20 border-gold-500/60 text-gold-400'
                      : 'bg-dark-800 border-dark-700 text-gray-400 hover:border-gold-500/40 hover:text-gray-300'
                  }`}
                >
                  {TAG_LABELS[tag]}
                </button>
              ))}
            </div>

            {/* Free-text */}
            <textarea
              value={newContent}
              onChange={e => setNewContent(e.target.value)}
              placeholder="Write a private note about this athlete..."
              rows={3}
              className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 resize-none"
            />

            <div className="flex justify-end">
              <button
                onClick={handleCreate}
                disabled={saving || !newContent.trim()}
                className="px-4 py-1.5 bg-gold-500 hover:bg-gold-400 disabled:bg-dark-800 disabled:text-gray-500 text-black text-sm font-semibold rounded-lg transition-colors"
              >
                {saving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>

          {/* ── Notes List ── */}
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          {loading ? (
            <p className="text-gray-500 text-sm text-center py-6">Loading notes...</p>
          ) : notes.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">
              No notes yet. Add one above.
            </p>
          ) : (
            <div className="space-y-3">
              {notes.map(note => (
                <NoteCard
                  key={note.noteId}
                  note={note}
                  isEditing={editingId === note.noteId}
                  editContent={editContent}
                  editTags={editTags}
                  editSaving={editSaving}
                  confirmingDelete={deleteTargetId === note.noteId}
                  onEdit={() => startEdit(note)}
                  onCancelEdit={cancelEdit}
                  onEditContentChange={setEditContent}
                  onEditTagToggle={tag => toggleTag(tag, editTags, setEditTags)}
                  onSaveEdit={() => handleUpdate(note.noteId)}
                  onRequestDelete={() => setDeleteTargetId(note.noteId)}
                  onCancelDelete={() => setDeleteTargetId(null)}
                  onConfirmDelete={() => handleDelete(note.noteId)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── NoteCard ──────────────────────────────────────────────────────────────────

interface NoteCardProps {
  note: CoachAthleteNote;
  isEditing: boolean;
  editContent: string;
  editTags: CoachAthleteNoteTag[];
  editSaving: boolean;
  confirmingDelete: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onEditContentChange: (v: string) => void;
  onEditTagToggle: (tag: CoachAthleteNoteTag) => void;
  onSaveEdit: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  isEditing,
  editContent,
  editTags,
  editSaving,
  confirmingDelete,
  onEdit,
  onCancelEdit,
  onEditContentChange,
  onEditTagToggle,
  onSaveEdit,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}) => {
  const updated = new Date(note.updatedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const tags = note.tags as CoachAthleteNoteTag[];

  if (isEditing) {
    return (
      <div className="bg-dark-700 rounded-xl p-4 space-y-3 border border-gold-500/30">
        <div className="flex flex-wrap gap-2">
          {ALL_TAGS.map(tag => (
            <button
              key={tag}
              onClick={() => onEditTagToggle(tag)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                editTags.includes(tag)
                  ? 'bg-gold-500/20 border-gold-500/60 text-gold-400'
                  : 'bg-dark-800 border-dark-700 text-gray-400 hover:border-gold-500/40 hover:text-gray-300'
              }`}
            >
              {TAG_LABELS[tag]}
            </button>
          ))}
        </div>
        <textarea
          value={editContent}
          onChange={e => onEditContentChange(e.target.value)}
          rows={3}
          className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500 resize-none"
        />
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancelEdit}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSaveEdit}
            disabled={editSaving || !editContent.trim()}
            className="px-4 py-1.5 bg-gold-500 hover:bg-gold-400 disabled:bg-dark-800 disabled:text-gray-500 text-black text-sm font-semibold rounded-lg transition-colors"
          >
            {editSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-700 rounded-xl p-4 space-y-2">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(tag => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-400"
            >
              {TAG_LABELS[tag] ?? tag}
            </span>
          ))}
        </div>
      )}
      <p className="text-sm text-gray-200 whitespace-pre-wrap">{note.content}</p>
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-gray-500">{updated}</span>
        {confirmingDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-400">Delete this note?</span>
            <button
              onClick={onCancelDelete}
              className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirmDelete}
              className="text-xs px-2 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"
            >
              Delete
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={onEdit}
              className="text-xs text-gray-500 hover:text-gold-400 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={onRequestDelete}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachAthleteNotesDrawer;
